import React from 'react';
import PropTypes from 'prop-types';

import { Link, Tooltip, Flexer } from '@ontruck/orc';

import { withFormikField } from 'utils/formik';
import { injectIntl } from 'react-intl';
import {
  DAY_HOLIDAY_EXTRA,
  PENALTY_EXTRA,
  BONUS_EXTRA,
  COST_INCREMENT_EXTRA,
  EXTRA_PERCENT,
  MANUAL_EXTRA,
  TENDER_EXTRA,
  NEGOTIATED_EXTRA,
  MANAGEMENT_FEE_EXTRA,
  EXTRA_NUMERIC,
} from 'modules/shipper-order/Form/constants';

import {
  BUNKER_ADJUSTMENT_FACTOR_EXTRA,
  INCREMENT_FOR_WEIGHT_OVERLOAD_EXTRA,
  INCREMENT_FOR_USING_WEIGHING_SCALE_EXTRA,
} from 'modules/shipper-order/Form/Intraser/tenantConstants';

import {
  StyledExtrasHeader,
  StyledFirstColumn,
  StyledExtrasGroup,
  ExtraElement,
} from 'modules/Common/form/extras';

import { ExtraCostRecommendations, getRecommendedExtraCost } from 'modules/recommended-extra-cost';

import {
  featurePreferenceToggle,
  DENY_MANUAL_EXTRA_COST,
  DENY_MANUAL_EXTRA_BONUS,
} from 'modules/preferences-manager/preferences';
import { IS_TENANT } from 'tenant/config';

const hasExtra = ({ values, extrasAmountsField }, extra) =>
  !!(values[extrasAmountsField] || []).find(({ concept }) => concept === extra);

const ExtrasSection = ({
  form,
  currency,
  activeExtras,
  isManual,
  cancellationValues,
  quote,
  extrasAmountsField,
  route,
  intl,
  ...props
}) => {
  const commonProps = {
    extrasAmountsField,
    quote,
    currency,
  };
  const labels = intl.messages.modules.journey.editForm.extras_section;
  const t = {
    concept: labels.concept,
    charged: labels.charged,
    [BONUS_EXTRA]: labels.BONUS_EXTRA,
    [PENALTY_EXTRA]: labels.PENALTY_EXTRA,
    [DAY_HOLIDAY_EXTRA]: labels.DAY_HOLIDAY_EXTRA,
    [COST_INCREMENT_EXTRA]: labels.COST_INCREMENT_EXTRA,
    [MANUAL_EXTRA]: labels.MANUAL_EXTRA,
    [TENDER_EXTRA]: labels.TENDER_EXTRA,
    [NEGOTIATED_EXTRA]: labels.NEGOTIATED_EXTRA,
    [MANAGEMENT_FEE_EXTRA]: labels.MANAGEMENT_FEE_EXTRA,
    [BUNKER_ADJUSTMENT_FACTOR_EXTRA]: labels.BUNKER_ADJUSTMENT_FACTOR_EXTRA,
    [INCREMENT_FOR_WEIGHT_OVERLOAD_EXTRA]: labels.INCREMENT_FOR_WEIGHT_OVERLOAD_EXTRA,
    [INCREMENT_FOR_USING_WEIGHING_SCALE_EXTRA]: labels.INCREMENT_FOR_USING_WEIGHING_SCALE_EXTRA,
    extra_cost_recommendations: labels.extra_cost_recommendations,
  };
  const { values } = form;

  const hastManualExtra = hasExtra({ values, extrasAmountsField }, MANUAL_EXTRA);
  const hasTenderExtra = hasExtra({ values, extrasAmountsField }, TENDER_EXTRA);

  const extraCostRecommendations = getRecommendedExtraCost(route);

  const isExtraCostEnabled = !featurePreferenceToggle(DENY_MANUAL_EXTRA_COST);
  const isBonusEnabled = !featurePreferenceToggle(DENY_MANUAL_EXTRA_BONUS);
  const showIncrementForOverload = activeExtras.includes(INCREMENT_FOR_WEIGHT_OVERLOAD_EXTRA);
  const showIncrementForUsingWeighingScale = activeExtras.includes(
    INCREMENT_FOR_USING_WEIGHING_SCALE_EXTRA
  );

  return (
    <div>
      <StyledExtrasGroup>
        <StyledExtrasHeader>
          <StyledFirstColumn p="XS" pLeft="S" pRight="S">
            {t.concept}
          </StyledFirstColumn>
          <Flexer.Child>{t.charged}</Flexer.Child>
        </StyledExtrasHeader>
        {hastManualExtra && !IS_TENANT && (
          <ExtraElement
            label={t[MANUAL_EXTRA]}
            extraName={MANUAL_EXTRA}
            disabled
            {...commonProps}
          />
        )}
        {hasTenderExtra && !IS_TENANT && (
          <ExtraElement
            label={t[TENDER_EXTRA]}
            extraName={TENDER_EXTRA}
            disabled
            {...commonProps}
          />
        )}
        {isBonusEnabled && !IS_TENANT && (
          <ExtraElement label={t[BONUS_EXTRA]} extraName={BONUS_EXTRA} {...commonProps} />
        )}

        <ExtraElement
          label={t[PENALTY_EXTRA]}
          extraName={PENALTY_EXTRA}
          isNegative
          {...commonProps}
        />

        {!IS_TENANT && (
          <ExtraElement
            label={t[DAY_HOLIDAY_EXTRA]}
            extraName={DAY_HOLIDAY_EXTRA}
            extraType={EXTRA_PERCENT}
            showAmount
            {...commonProps}
          />
        )}
        {showIncrementForOverload && (
          <ExtraElement
            label={t[INCREMENT_FOR_WEIGHT_OVERLOAD_EXTRA]}
            extraName={INCREMENT_FOR_WEIGHT_OVERLOAD_EXTRA}
            extraType={EXTRA_NUMERIC}
            showAmount
            {...commonProps}
          />
        )}
        {showIncrementForUsingWeighingScale && (
          <ExtraElement
            label={t[INCREMENT_FOR_USING_WEIGHING_SCALE_EXTRA]}
            extraName={INCREMENT_FOR_USING_WEIGHING_SCALE_EXTRA}
            extraType={EXTRA_NUMERIC}
            showAmount
            {...commonProps}
          />
        )}
        {isExtraCostEnabled && !IS_TENANT && (
          <ExtraElement
            label={t[COST_INCREMENT_EXTRA]}
            extraName={COST_INCREMENT_EXTRA}
            {...commonProps}
          >
            {!!extraCostRecommendations && (
              <Tooltip
                component={<ExtraCostRecommendations route={route} currency={currency} />}
                skin="light"
                position={'top'}
                bodySize="M"
              >
                <Link text={t.extra_cost_recommendations} skin="secondary" />
              </Tooltip>
            )}
          </ExtraElement>
        )}
      </StyledExtrasGroup>
    </div>
  );
};

ExtrasSection.propTypes = {
  form: PropTypes.object.isRequired,
  currency: PropTypes.string.isRequired,
  activeExtras: PropTypes.array,
  isManual: PropTypes.bool,
  cancellationValues: PropTypes.array,
  quote: PropTypes.object,
  extrasAmountsField: PropTypes.oneOf(['extra_costs', 'extra_prices']),
};

ExtrasSection.defaultProps = {
  isManual: false,
  cancellationValues: [],
  quote: {},
  extrasAmountsField: 'extra_costs',
};

export default withFormikField(injectIntl(ExtrasSection));
